import styles from "./MyProfile.module.css";
import useAuthStore from "../../store/authStore";
import { useState, useRef } from "react";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import avatarImage from "../../assets/icons/avatar.png";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import { toast } from "react-toastify";

const MyProfile = () => {
  const { user, userMeta, setUserMeta } = useAuthStore();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(userMeta?.profile_picture_url || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only image files (JPEG, PNG, GIF, WebP) are allowed!");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB!");
        return;
      }

      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    const formData = new FormData();
    formData.append("profile_picture", selectedFile);
    formData.append("user_id", user.id);

    try {
      setUploading(true);
      const response = await api.post(
        ENDPOINTS.UPLOAD_PROFILE_PICTURE,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success("Profile picture uploaded successfully!");
        setUserMeta({
          ...userMeta,
          profile_picture_url: response.data.profile_picture_url,
        });
        setSelectedFile(null);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload profile picture");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await api.post(ENDPOINTS.DELETE_PROFILE_PICTURE, {
        user_id: user.id,
      });

      if (response.data.success) {
        toast.success("Profile picture deleted successfully!");
        setPreview(null);
        setSelectedFile(null);
        setUserMeta({
          ...userMeta,
          profile_picture_url: null,
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete profile picture");
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreview(userMeta?.profile_picture_url || null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemovePreview = () => {
    setSelectedFile(null);
    setPreview(userMeta?.profile_picture_url || null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>My Profile</h2>

      {/* Profile Picture Section */}
      <div className={styles.profileSection}>
        <label className={styles.sectionLabel}>Profile Picture</label>

        <div className={styles.profileContent}>
          <div className={styles.featuredImageBox}>
            {preview ? (
              <>
                <img
                  src={preview}
                  alt="Profile"
                  className={styles.profileImage}
                  onError={(e) => {
                    e.target.src = avatarImage;
                  }}
                />
                <button
                  type="button"
                  disabled={uploading}
                  title="Remove image"
                  className={styles.removeImageButton}
                  onClick={handleRemovePreview}
                >
                  ✕
                </button>
              </>
            ) : (
              <div className={styles.iconPlaceholderContainer}>
                <img
                  src={placeholderImage}
                  alt="Placeholder"
                  className={styles.iconPlaceholder}
                />
                <label className={styles.uploadLink}>
                  <span className={styles.plusIcon}>+</span> Choose Profile
                  Picture
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    disabled={uploading}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            {selectedFile && (
              <>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className={`${styles.submitButton} ${
                    uploading ? styles.submitting : ""
                  }`}
                >
                  <span>{uploading ? "Uploading..." : "Upload Photo"}</span>
                  <span className={styles.submitIcon}>✓</span>
                </button>
                <button
                  onClick={handleCancel}
                  className={styles.cancelButton}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </>
            )}

            {userMeta?.profile_picture_url && !selectedFile && (
              <button
                onClick={handleDelete}
                className={styles.deleteButton}
                disabled={uploading}
              >
                <span>Delete Photo</span>
                <span className={styles.deleteIcon}>🗑</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Profile Information Grid */}
      <div className={styles.grid}>
        <ProfileRow label="Username" value={user.username} />
        <ProfileRow label="Email" value={user.email} />
        <ProfileRow label="First Name" value={user.first_name} />
        <ProfileRow label="Last Name" value={user.last_name} />
        <ProfileRow label="Display Name" value={user.display_name} />

        <ProfileRow
          label="Membership Active"
          value={userMeta.is_membership_active ? "Yes" : "No"}
        />
        <ProfileRow
          label="Created At"
          value={new Date(userMeta.created_at).toLocaleString()}
        />
        <ProfileRow
          label="Updated At"
          value={new Date(userMeta.updated_at).toLocaleString()}
        />
        <ProfileRow label="User Role" value={user?.roles[0]} />
      </div>
    </div>
  );
};

const ProfileRow = ({ label, value }) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    <span className={styles.value}>{value}</span>
  </div>
);

export default MyProfile;
